import { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { locationService } from '../services'

const LOCATION_PAGE_SIZE = 10
const LOCATION_SEARCH_DEBOUNCE_MS = 400
const LOCATION_SCROLL_THROTTLE_MS = 200
const LOCATION_LISTBOX_STYLE = { maxHeight: 240 }

export function formatLocationLabel(loc) {
  if (!loc) return ''
  const name = loc.name || ''
  const code = loc.code || ''
  if (name && code) return `${name} (${code})`
  return name || code || String(loc.id ?? '')
}

function mergeLocationLists(existing, incoming) {
  const byId = new Map(existing.map((loc) => [loc.id, loc]))
  for (const loc of incoming) {
    byId.set(loc.id, loc)
  }
  return Array.from(byId.values())
}

export function usePaginatedLocationSearch({ open, onError }) {
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const draftSearchRef = useRef('')
  const searchTimerRef = useRef(null)
  const inFlightRef = useRef(false)
  const fetchIdRef = useRef(0)
  const lastScrollLoadRef = useRef(0)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const clearSearchTimer = useCallback(() => {
    if (searchTimerRef.current == null) return
    clearTimeout(searchTimerRef.current)
    searchTimerRef.current = null
  }, [])

  const commitSearchQuery = useCallback((raw) => {
    const next = String(raw || '').trim()
    setSearchQuery((prev) => (prev === next ? prev : next))
  }, [])

  const scheduleSearch = useCallback(
    (raw) => {
      draftSearchRef.current = raw
      clearSearchTimer()
      if (!String(raw || '').trim()) {
        commitSearchQuery('')
        return
      }
      searchTimerRef.current = setTimeout(() => {
        searchTimerRef.current = null
        commitSearchQuery(draftSearchRef.current)
      }, LOCATION_SEARCH_DEBOUNCE_MS)
    },
    [clearSearchTimer, commitSearchQuery],
  )

  const resetSelection = useCallback(() => {
    setSelected(null)
    setInputValue('')
    draftSearchRef.current = ''
    clearSearchTimer()
    setSearchQuery('')
    setOptions([])
    setPage(1)
    setHasMore(true)
  }, [clearSearchTimer])

  const loadPage = useCallback(async (pageToLoad, search, append) => {
    // Append waits; replace/search supersedes any in-flight request.
    if (append && inFlightRef.current) return
    inFlightRef.current = true
    const fetchId = ++fetchIdRef.current
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)

      const trimmed = String(search || '').trim()
      const data = await locationService.getLocations({
        page: pageToLoad,
        limit: LOCATION_PAGE_SIZE,
        ...(trimmed ? { search: trimmed } : {}),
      })
      if (fetchId !== fetchIdRef.current) return

      const items = Array.isArray(data?.items) ? data.items : []
      setOptions((prev) => (append ? mergeLocationLists(prev, items) : items))
      setPage(data?.pagination?.page ?? pageToLoad)
      setHasMore(Boolean(data?.pagination?.hasNextPage))
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return
      if (!append) {
        setOptions([])
        onErrorRef.current?.(
          err.response?.data?.message ||
            err.message ||
            'Failed to load distribution centers',
        )
      }
      setHasMore(false)
    } finally {
      if (fetchId === fetchIdRef.current) {
        inFlightRef.current = false
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [])

  useEffect(() => () => clearSearchTimer(), [clearSearchTimer])

  useEffect(() => {
    if (!open) return
    setOptions([])
    setPage(1)
    setHasMore(true)
    loadPage(1, searchQuery, false)
  }, [open, searchQuery, loadPage])

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return
    loadPage(page + 1, searchQuery, true)
  }, [hasMore, page, searchQuery, loadPage])

  const handleListScroll = useCallback(
    (event) => {
      const now = Date.now()
      if (now - lastScrollLoadRef.current < LOCATION_SCROLL_THROTTLE_MS) return
      const node = event.currentTarget
      const nearBottom =
        node.scrollTop + node.clientHeight >= node.scrollHeight - 48
      if (!nearBottom) return
      lastScrollLoadRef.current = now
      loadMore()
    },
    [loadMore],
  )

  const selectLocation = useCallback(
    (value) => {
      setSelected(value)
      setInputValue(value ? formatLocationLabel(value) : '')
      scheduleSearch('')
    },
    [scheduleSearch],
  )

  const handleInputChange = useCallback(
    (value, reason) => {
      setInputValue(value)
      if (reason === 'input' || reason === 'clear') {
        scheduleSearch(reason === 'clear' ? '' : value)
      }
    },
    [scheduleSearch],
  )

  const locationId = selected?.id != null ? String(selected.id) : ''

  return {
    options,
    selected,
    locationId,
    inputValue,
    searchQuery,
    loading,
    loadingMore,
    resetSelection,
    selectLocation,
    handleInputChange,
    handleListScroll,
  }
}

export function DistributionCenterField({
  options,
  selected,
  inputValue,
  loading,
  loadingMore,
  disabled,
  onSelect,
  onInputChange,
  onListScroll,
  /** Raise list popper above a nested dialog (Excel preview). */
  elevatePopper = false,
}) {
  const busy = loading || loadingMore
  return (
    <Autocomplete
      fullWidth
      sx={{ mb: 2 }}
      options={options}
      loading={busy}
      disabled={disabled}
      value={selected}
      inputValue={inputValue}
      filterOptions={(opts) => opts}
      getOptionLabel={formatLocationLabel}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      noOptionsText={
        loading
          ? 'Loading distribution centers…'
          : 'No distribution centers found'
      }
      onChange={(_, value) => onSelect(value)}
      onInputChange={(_, value, reason) => onInputChange(value, reason)}
      ListboxProps={{
        onScroll: onListScroll,
        style: LOCATION_LISTBOX_STYLE,
      }}
      componentsProps={
        elevatePopper
          ? {
              popper: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 4 },
              },
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Distribution Center"
          required
          placeholder="Search by name or code"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {busy ? (
                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

